/// <reference path="../../../types/axios-fix.d.ts" />
import { BaseService, httpClientService, authInterceptorService, errorHandlerService, cacheService, ApiResponse } from '../core';

/**
 * Subscription Plan interface
 */
export interface SubscriptionPlan {
  id: string;
  name: string;
  description: string;
  type: number;
  pricing: {
    currency?: string;
    monthly: {
      pricePerMonth: number;  // Price in cents
      stripePriceId: string;
    };
    yearly: {
      pricePerYear: number;  // Price in cents
      pricePerMonth: number;  // Monthly equivalent in cents (for display)
      stripePriceId: string;
    };
    // Legacy fields for backwards compatibility
    basePriceCents?: number;
    perUserPriceCents?: number;
    perGbPriceCents?: number;
    stripePriceId?: string;
  };
  limits: {
    maxUsers: number;
    maxStorageGb: number;
    maxTeams: number;
    maxApiCalls: number;
  };
  features: string[];
  isActive?: boolean;
}

/**
 * Subscription interface
 */
export interface Subscription {
  _id: string;
  id?: string;
  name: string;
  planId: string;
  planName?: string;
  organizationId: string;
  status: 'active' | 'inactive' | 'cancelled' | 'expired' | 'trial';
  startDate: Date;
  endDate?: Date;
  autoRenew: boolean;
  stripeSubscriptionId?: string;
  stripeCustomerId?: string;
  currentPeriodStart?: Date;
  currentPeriodEnd?: Date;
  cancelAtPeriodEnd?: boolean;
  addOns?: {
    extraUsers?: number;
    extraStorage?: number;
    extraTeams?: number;
    extraApiCalls?: number;
  };
}

/**
 * Payment Method interface
 */
export interface PaymentMethod {
  id: string;
  type: 'card' | 'bank_account' | 'paypal';
  brand?: string;
  last4: string;
  expiryMonth?: number;
  expiryYear?: number;
  isDefault: boolean;
  billingDetails?: {
    name?: string;
    email?: string;
    phone?: string;
    address?: {
      line1?: string;
      line2?: string;
      city?: string;
      state?: string;
      postalCode?: string;
      country?: string;
    };
  };
}

/**
 * Subscription Preview interface
 */
export interface SubscriptionPreview {
  immediate_charge_cents: number;
  recurring_total_cents: number;
  subtotal_cents: number;
  tax_cents: number;
  currency: string;
  proration_cents: number;
  next_billing_date: number;
  period_start: string;
  period_end: string;
  line_items: Array<{
    description: string;
    amount_cents: number;
    quantity: number;
    is_proration: boolean;
    period_start: string;
    period_end: string;
    stripe_price_id: string;
  }>;
}

/**
 * Invoice interface
 */
export interface Invoice {
  id: string;
  number: string;
  status: 'draft' | 'open' | 'paid' | 'uncollectible' | 'void';
  amount: number;
  currency: string;
  dueDate?: Date;
  paidAt?: Date;
  periodStart: Date;
  periodEnd: Date;
  description?: string;
  lines?: {
    description: string;
    amount: number;
    quantity?: number;
  }[];
  downloadUrl?: string;
  stripeInvoiceId?: string;
}

/**
 * Usage Metrics interface
 */
export interface UsageMetrics {
  users: {
    current: number;
    limit: number;
    percentage: number;
  };
  storage: {
    current: number;
    limit: number;
    percentage: number;
    unit: 'GB' | 'MB';
  };
  teams: {
    current: number;
    limit: number;
    percentage: number;
  };
  apiCalls?: {
    current: number;
    limit: number;
    percentage: number;
    period: 'month' | 'day';
  };
}

/**
 * Usage History interface
 */
export interface UsageHistory {
  date: string;
  value: number;
  resourceType: 'api_calls' | 'storage' | 'users' | 'teams';
  unit?: string;
}

/**
 * Create Subscription Request
 */
export interface CreateSubscriptionRequest {
  name: string;
  planId: string;
  autoRenew: boolean;
  organizationId: string;
  paymentMethodId?: string;
  startTrial?: boolean;
  billingInterval?: 'monthly' | 'yearly';  // Billing frequency
}

/**
 * Update Subscription Add-Ons Request
 */
export interface UpdateSubscriptionAddOnsRequest {
  addOns: Array<{
    priceId: string;
    quantity: number;
  }>;
  paymentMethodId?: string;
}

/**
 * Subscription Service
 * 
 * Handles all subscription and payment-related operations including:
 * - Subscription management (create, update, cancel)
 * - Payment method management
 * - Invoice management
 * - Usage tracking and metrics
 * - Billing and checkout sessions
 * 
 * Extracted from the monolithic API service.
 */
export class SubscriptionService extends BaseService {
  constructor() {
    super(httpClientService, authInterceptorService, errorHandlerService, cacheService);
  }

  // ===== SUBSCRIPTION PLAN OPERATIONS =====

  /**
   * Get available subscription plans
   */
  async getSubscriptionPlans(): Promise<any[]> {
    try {
      console.log('Fetching subscription plans');

      const response = await this.getCached<any>('/subscription/plans');

      // Handle wrapped response format from V2 API
      const plans = response?.data?.plans || response?.plans || response?.data || response || [];

      console.log('Fetched plans:', plans);

      // Return plans as-is from API (they already have the correct structure)
      return plans;
    } catch (error) {
      console.error('Error fetching subscription plans:', error);
      throw this.errorHandler.handleError(error, 'SubscriptionService.getSubscriptionPlans');
    }
  }

  // ===== SUBSCRIPTION CRUD OPERATIONS =====

  /**
   * Get subscription by ID
   */
  async getSubscriptionById(subscriptionId: string): Promise<Subscription> {
    try {
      console.log(`Fetching subscription: ${subscriptionId}`);
      
      const response = await this.get<any>(`/subscriptions/${subscriptionId}`);
      
      return this.normalizeSubscription(response);
    } catch (error) {
      console.error(`Error fetching subscription ${subscriptionId}:`, error);
      throw this.errorHandler.handleError(error, `SubscriptionService.getSubscriptionById(${subscriptionId})`);
    }
  }

  /**
   * Get organization subscriptions (paginated)
   */
  async getOrganizationSubscriptions(
    organizationId: string,
    options?: {
      page?: number;
      limit?: number;
      status?: 'active' | 'inactive' | 'cancelled' | 'expired' | 'trial';
    }
  ): Promise<{
    subscriptions: Subscription[];
    pagination?: {
      totalCount: number;
      pageSize: number;
      currentPage: number;
      totalPages: number;
      hasNextPage: boolean;
      hasPreviousPage: boolean;
    };
  }> {
    try {
      console.log(`Fetching subscriptions for organization ${organizationId}`, options);

      const params: any = {
        page: options?.page || 1,
        limit: options?.limit || 100,
      };

      if (options?.status) {
        params.status = options.status;
      }

      const response = await this.requestWithMetadata<any[]>(
        'GET',
        `/organizations/${organizationId}/subscriptions`,
        undefined,
        { params }
      );

      console.log(`🔍 SubscriptionService: Raw API response for org ${organizationId}:`, {
        hasData: !!response?.data,
        hasMetadata: !!response?.metadata,
        dataLength: Array.isArray(response?.data) ? response.data.length : undefined,
        paginationInMetadata: !!response?.metadata?.pagination,
        response
      });

      // Handle response from requestWithMetadata which returns { data, metadata }
      const subscriptions = response?.data || [];
      const pagination = response?.metadata?.pagination;

      console.log(`🔍 SubscriptionService: Extracted ${subscriptions.length} subscriptions, pagination:`, pagination);

      // Normalize and filter subscriptions
      const normalizedSubs = subscriptions.map((sub: any) => {
        const normalized = this.normalizeSubscription(sub);
        // If API didn't provide organizationId, set it explicitly
        if (!normalized.organizationId) {
          normalized.organizationId = organizationId;
        }
        return normalized;
      });

      console.log(`🔍 SubscriptionService: Normalized ${normalizedSubs.length} subscriptions`);

      // DEFENSIVE: Filter to ensure only subscriptions for the requested org are returned
      // This shouldn't be necessary (backend should filter correctly), but protects against backend bugs
      const filteredSubs = normalizedSubs.filter((sub: any) => sub.organizationId === organizationId);

      // Log warning if backend returned incorrect data
      const incorrectCount = normalizedSubs.length - filteredSubs.length;
      if (incorrectCount > 0) {
        console.error(
          `⚠️ Backend returned ${incorrectCount} subscriptions for WRONG organizations!`,
          `Requested: ${organizationId}, Got ${normalizedSubs.length} total, Filtered to ${filteredSubs.length}`,
          `Wrong subs:`, normalizedSubs.filter((sub: any) => sub.organizationId !== organizationId)
        );
      }

      return {
        subscriptions: filteredSubs,
        pagination
      };
    } catch (error) {
      console.warn(`Failed to fetch subscriptions for organization ${organizationId}:`, error);

      // For non-critical errors (404, 500), return empty array
      // This allows the app to continue functioning
      const status = this.errorHandler.getErrorStatus(error);
      if (status === 404 || status === 500 || status === 503) {
        return { subscriptions: [] };
      }

      // For auth errors, re-throw so the app can handle authentication
      if (status === 401 || status === 403) {
        throw this.errorHandler.handleError(error, `SubscriptionService.getOrganizationSubscriptions(${organizationId})`);
      }

      // Default to empty array for other errors
      return { subscriptions: [] };
    }
  }

  /**
   * Create a new subscription
   */
  async createSubscription(data: CreateSubscriptionRequest): Promise<Subscription> {
    try {
      console.log('Creating subscription:', data);

      const requestBody: any = {
        plan_id: data.planId,
        reference_name: data.name
      };

      if (data.paymentMethodId) {
        requestBody.payment_method_id = data.paymentMethodId;
      }

      if (data.startTrial !== undefined) {
        requestBody.start_trial = data.startTrial;
      }

      // Add billing interval (defaults to monthly if not specified)
      if (data.billingInterval) {
        requestBody.billing_interval = data.billingInterval === 'yearly' ? 1 : 0;  // 0 = BILLING_MONTHLY, 1 = BILLING_YEARLY
      }

      // Stripe operations need extended timeout (60s) due to payment processing and subscription setup
      const response = await this.post<any>(
        `/organizations/${data.organizationId}/subscription`,
        requestBody,
        { timeout: 60000 }
      );

      // Invalidate cache
      this.invalidateCache('organization');

      return this.normalizeSubscription(response);
    } catch (error) {
      console.error('Error creating subscription:', error);
      throw this.errorHandler.handleError(error, 'SubscriptionService.createSubscription');
    }
  }

  /**
   * Cancel a subscription
   */
  async cancelSubscription(subscriptionId: string, reason?: string): Promise<Subscription> {
    try {
      console.log(`Cancelling subscription: ${subscriptionId}`);

      // Use POST /cancel endpoint for period-end cancellation
      // Stripe operations need extended timeout (60s) due to payment processing
      const response = await this.post<any>(`/subscriptions/${subscriptionId}/cancel`, {
        reason: reason || 'User requested cancellation'
      }, { timeout: 60000 });

      // Invalidate cache
      this.invalidateCache('organization');

      return this.normalizeSubscription(response);
    } catch (error) {
      console.error(`Error cancelling subscription ${subscriptionId}:`, error);
      throw this.errorHandler.handleError(error, `SubscriptionService.cancelSubscription(${subscriptionId})`);
    }
  }

  /**
   * Reactivate a cancelled subscription
   */
  async reactivateSubscription(subscriptionId: string): Promise<Subscription> {
    try {
      console.log(`Reactivating subscription: ${subscriptionId}`);

      // Stripe operations need extended timeout (60s) due to payment processing
      const response = await this.post<any>(`/subscriptions/${subscriptionId}/reactivate`, {}, { timeout: 60000 });

      // Invalidate cache
      this.invalidateCache('organization');

      // Extract subscription from response wrapper
      const subscription = response.subscription || response;
      return this.normalizeSubscription(subscription);
    } catch (error) {
      console.error(`Error reactivating subscription ${subscriptionId}:`, error);
      throw this.errorHandler.handleError(error, `SubscriptionService.reactivateSubscription(${subscriptionId})`);
    }
  }

  /**
   * Delete an expired subscription
   */
  async deleteSubscription(subscriptionId: string): Promise<void> {
    try {
      console.log(`Deleting subscription: ${subscriptionId}`);

      await this.delete(`/subscriptions/${subscriptionId}`);

      // Invalidate cache
      this.invalidateCache('organization');
    } catch (error) {
      console.error(`Error deleting subscription ${subscriptionId}:`, error);
      throw this.errorHandler.handleError(error, `SubscriptionService.deleteSubscription(${subscriptionId})`);
    }
  }

  /**
   * Update subscription auto-renewal
   */
  async updateSubscriptionAutoRenew(subscriptionId: string, autoRenew: boolean): Promise<Subscription> {
    try {
      console.log(`Updating auto-renew for subscription ${subscriptionId}: ${autoRenew}`);
      
      const response = await this.put<any>(
        `/subscriptions/${subscriptionId}/auto-renew`,
        { autoRenew }
      );
      
      // Invalidate cache
      this.invalidateCache('organization');
      
      return this.normalizeSubscription(response);
    } catch (error) {
      console.error(`Error updating auto-renew for subscription ${subscriptionId}:`, error);
      throw this.errorHandler.handleError(error, `SubscriptionService.updateSubscriptionAutoRenew(${subscriptionId})`);
    }
  }

  /**
   * Update subscription add-ons
   */
  async listAvailableAddOns(): Promise<{
    userPackages: Array<{
      type: string;
      packSize: number;
      name: string;
      description: string;
      pricing: {
        monthly: {
          priceCents: number;
          stripePriceId: string;
        };
        yearly: {
          priceCents: number;
          stripePriceId: string;
          displayPricePerMonth?: number;
        };
      };
      recommended: boolean;
      // Legacy fields for backward compatibility
      monthlyPriceCents: number;
      stripePriceId: string;
    }>;
    storagePackages: Array<{
      type: string;
      packSize: number;
      name: string;
      description: string;
      pricing: {
        monthly: {
          priceCents: number;
          stripePriceId: string;
        };
        yearly: {
          priceCents: number;
          stripePriceId: string;
          displayPricePerMonth?: number;
        };
      };
      recommended: boolean;
      // Legacy fields for backward compatibility
      monthlyPriceCents: number;
      stripePriceId: string;
    }>;
  }> {
    try {
      console.log('Fetching available add-ons');

      const response = await this.get<any>('/subscription/available-addons');

      // Transform snake_case to camelCase
      const transformPackage = (pkg: any) => ({
        type: pkg.type,
        packSize: pkg.pack_size,
        name: pkg.name,
        description: pkg.description,
        pricing: {
          monthly: {
            priceCents: pkg.pricing?.monthly?.price_cents || pkg.monthly_price_cents || 0,
            stripePriceId: pkg.pricing?.monthly?.stripe_price_id || pkg.stripe_price_id || '',
          },
          yearly: {
            priceCents: pkg.pricing?.yearly?.price_cents || 0,
            stripePriceId: pkg.pricing?.yearly?.stripe_price_id || '',
            displayPricePerMonth: pkg.pricing?.yearly?.display_price_per_month,
          },
        },
        recommended: pkg.recommended,
        // Legacy fields for backward compatibility
        monthlyPriceCents: pkg.monthly_price_cents || pkg.pricing?.monthly?.price_cents || 0,
        stripePriceId: pkg.stripe_price_id || pkg.pricing?.monthly?.stripe_price_id || '',
      });

      return {
        userPackages: (response.user_packages || []).map(transformPackage),
        storagePackages: (response.storage_packages || []).map(transformPackage)
      };
    } catch (error) {
      console.error('Error fetching available add-ons:', error);
      throw this.errorHandler.handleError(error, 'SubscriptionService.listAvailableAddOns()');
    }
  }

  async updateSubscriptionAddOns(
    subscriptionId: string,
    addOns: UpdateSubscriptionAddOnsRequest
  ): Promise<Subscription> {
    try {
      console.log(`Updating add-ons for subscription ${subscriptionId}:`, addOns);

      // Stripe operations need extended timeout (60s) due to invoice generation and payment processing
      const response = await this.post<any>(
        `/subscriptions/${subscriptionId}/add-ons`,
        addOns,
        { timeout: 60000 }
      );

      // Invalidate cache
      this.invalidateCache('organization');

      return this.normalizeSubscription(response);
    } catch (error) {
      console.error(`Error updating add-ons for subscription ${subscriptionId}:`, error);
      throw this.errorHandler.handleError(error, `SubscriptionService.updateSubscriptionAddOns(${subscriptionId})`);
    }
  }

  // ===== USAGE OPERATIONS =====

  /**
   * Get subscription usage
   */
  async getSubscriptionUsage(subscriptionId: string): Promise<{
    teamsCount: number;
    activeUsers: number;
    storageUsed: number;
  }> {
    try {
      console.log(`Fetching usage for subscription ${subscriptionId}`);
      
      const response = await this.getCached<any>(
        `/subscriptions/${subscriptionId}/usage`
      );
      
      return {
        teamsCount: response.teamsCount || response.teams_count || 0,
        activeUsers: response.activeUsers || response.active_users || 0,
        storageUsed: response.storageUsed || response.storage_used || 0
      };
    } catch (error) {
      console.error(`Error fetching usage for subscription ${subscriptionId}:`, error);
      throw this.errorHandler.handleError(error, `SubscriptionService.getSubscriptionUsage(${subscriptionId})`);
    }
  }

  /**
   * Get organization usage metrics
   */
  async getUsageMetrics(organizationId: string): Promise<UsageMetrics> {
    try {
      console.log(`Fetching usage metrics for organization ${organizationId}`);
      
      const response = await this.getCached<any>(
        `/organizations/${organizationId}/usage/metrics`
      );
      
      return response;
    } catch (error) {
      console.error(`Error fetching usage metrics for organization ${organizationId}:`, error);
      throw this.errorHandler.handleError(error, `SubscriptionService.getUsageMetrics(${organizationId})`);
    }
  }

  /**
   * Get usage history
   */
  async getUsageHistory(
    organizationId: string,
    resourceType: 'api_calls' | 'storage' | 'users' | 'teams',
    months: number = 6
  ): Promise<UsageHistory[]> {
    try {
      console.log(`Fetching usage history for organization ${organizationId}`);
      
      const response = await this.getCached<UsageHistory[]>(
        `/organizations/${organizationId}/usage/history`,
        { resourceType, months }
      );
      
      return response || [];
    } catch (error) {
      console.error(`Error fetching usage history for organization ${organizationId}:`, error);
      throw this.errorHandler.handleError(error, `SubscriptionService.getUsageHistory(${organizationId})`);
    }
  }

  /**
   * Check usage limits
   */
  async checkUsageLimits(organizationId: string): Promise<{
    withinLimits: boolean;
    approaching: boolean;
    nearingLimits: string[];
    exceededLimits: string[];
  }> {
    try {
      console.log(`Checking usage limits for organization ${organizationId}`);
      
      const response = await this.get<any>(
        `/organizations/${organizationId}/usage/check-limits`
      );
      
      return {
        withinLimits: response.withinLimits || response.within_limits || true,
        approaching: response.approaching || false,
        nearingLimits: response.nearingLimits || response.nearing_limits || [],
        exceededLimits: response.exceededLimits || response.exceeded_limits || []
      };
    } catch (error) {
      console.error(`Error checking usage limits for organization ${organizationId}:`, error);
      throw this.errorHandler.handleError(error, `SubscriptionService.checkUsageLimits(${organizationId})`);
    }
  }

  // ===== INVOICE OPERATIONS =====

  /**
   * List invoices for an organization
   */
  async listInvoices(
    organizationId: string,
    options?: {
      limit?: number;
      startingAfter?: string;
      endingBefore?: string;
      status?: 'draft' | 'open' | 'paid' | 'uncollectible' | 'void';
    }
  ): Promise<{
    invoices: Invoice[];
    hasMore: boolean;
  }> {
    try {
      console.log(`Fetching invoices for organization ${organizationId}`);
      
      const response = await this.get<any>(
        `/organizations/${organizationId}/invoices`,
        { params: options }
      );
      
      return {
        invoices: (response.invoices || response.data || []).map(this.normalizeInvoice),
        hasMore: response.hasMore || response.has_more || false
      };
    } catch (error) {
      console.error(`Error fetching invoices for organization ${organizationId}:`, error);
      throw this.errorHandler.handleError(error, `SubscriptionService.listInvoices(${organizationId})`);
    }
  }

  /**
   * Get a specific invoice
   */
  async getInvoice(organizationId: string, invoiceId: string): Promise<Invoice> {
    try {
      console.log(`Fetching invoice ${invoiceId} for organization ${organizationId}`);
      
      const response = await this.get<any>(
        `/organizations/${organizationId}/invoices/${invoiceId}`
      );
      
      return this.normalizeInvoice(response);
    } catch (error) {
      console.error(`Error fetching invoice ${invoiceId}:`, error);
      throw this.errorHandler.handleError(error, `SubscriptionService.getInvoice(${invoiceId})`);
    }
  }

  /**
   * Get billing history (legacy, use listInvoices instead)
   */
  async getBillingHistory(subscriptionId: string): Promise<Invoice[]> {
    try {
      console.log(`Fetching billing history for subscription ${subscriptionId}`);
      
      const response = await this.getCached<any[]>(
        `/api/subscriptions/${subscriptionId}/invoices`
      );
      
      return (response || []).map(this.normalizeInvoice);
    } catch (error) {
      console.error(`Error fetching billing history for subscription ${subscriptionId}:`, error);
      throw this.errorHandler.handleError(error, `SubscriptionService.getBillingHistory(${subscriptionId})`);
    }
  }

  /**
   * Update subscription reference name
   */
  async updateSubscriptionReferenceName(
    subscriptionId: string,
    referenceName: string
  ): Promise<Subscription> {
    try {
      console.log(`Updating reference name for subscription ${subscriptionId}: ${referenceName}`);
      
      const response = await this.patch<any>(
        `/subscriptions/${subscriptionId}/reference-name`,
        { reference_name: referenceName }
      );
      
      // Invalidate cache
      this.invalidateCache('organization');
      
      return this.normalizeSubscription(response);
    } catch (error) {
      console.error(`Error updating reference name for subscription ${subscriptionId}:`, error);
      throw this.errorHandler.handleError(error, `SubscriptionService.updateSubscriptionReferenceName(${subscriptionId})`);
    }
  }

  /**
   * Assign subscription to team
   */
  async assignSubscriptionToTeam(
    teamId: string,
    subscriptionId: string
  ): Promise<void> {
    try {
      console.log(`Assigning subscription ${subscriptionId} to team ${teamId}`);
      
      await this.put<any>(
        `/teams/${teamId}/subscription`,
        { subscription_id: subscriptionId }
      );

      // Invalidate cache for both organization and team data
      this.invalidateCache('organization');
      this.invalidateCache('team');
    } catch (error) {
      console.error(`Error assigning subscription ${subscriptionId} to team ${teamId}:`, error);
      throw this.errorHandler.handleError(error, `SubscriptionService.assignSubscriptionToTeam(${teamId}, ${subscriptionId})`);
    }
  }

  /**
   * Remove subscription from team
   */
  async removeSubscriptionFromTeam(teamId: string): Promise<void> {
    try {
      console.log(`Removing subscription from team ${teamId}`);
      
      await this.delete<any>(`/teams/${teamId}/subscription`);

      // Invalidate cache for both organization and team data
      this.invalidateCache('organization');
      this.invalidateCache('team');
    } catch (error) {
      console.error(`Error removing subscription from team ${teamId}:`, error);
      throw this.errorHandler.handleError(error, `SubscriptionService.removeSubscriptionFromTeam(${teamId})`);
    }
  }

  // ===== HELPER METHODS =====

  /**
   * Build features list from API limits response
   */
  private buildFeaturesList(limits: any): string[] {
    const features: string[] = [];
    
    if (limits?.aiFeaturesEnabled) {
      features.push('AI Features');
    }
    
    if (limits?.analyticsEnabled) {
      features.push('Advanced Analytics');
    }
    
    if (limits?.prioritySupport) {
      features.push('Priority Support');
    }
    
    if (limits?.maxFileSizeMb) {
      features.push(`${limits.maxFileSizeMb}MB file upload limit`);
    }
    
    if (limits?.enabledIntegrations && limits.enabledIntegrations.length > 0) {
      features.push(`${limits.enabledIntegrations.length} integrations`);
    }

    return features;
  }

  /**
   * Preview subscription change with accurate Stripe pricing
   * @param subscriptionId - Existing subscription ID (for add-ons)
   * @param organizationId - Organization ID (for new subscriptions)
   * @param planId - Plan ID (for new subscriptions)
   * @param billingInterval - Billing interval ('monthly' | 'yearly')
   * @param addOns - Array of add-ons to preview
   * @returns Subscription preview with accurate pricing
   */
  async previewSubscriptionChange(
    subscriptionId?: string,
    organizationId?: string,
    planId?: string,
    billingInterval?: 'monthly' | 'yearly',
    addOns?: Array<{ stripePriceId: string; quantity: number }>
  ): Promise<SubscriptionPreview> {
    // The post<T>() method already extracts the data field from the API response
    // So we receive the SubscriptionPreview object directly, not wrapped in ApiResponse
    return this.post<SubscriptionPreview>(
      '/subscriptions/preview',
      {
        subscriptionId,
        organizationId,
        planId,
        billingInterval,
        addOns
      }
    );
  }

  /**
   * Normalize subscription data from API response
   */
  private normalizeSubscription(subData: any): any {
    // Keep all original fields from the API response
    // and only normalize the fields that need it
    return {
      ...subData, // Keep all original fields
      _id: subData.id || subData._id,
      id: subData.id || subData._id,
      name: subData.name || subData.reference_name || subData.referenceName,
      planId: subData.planId || subData.plan_id,
      planName: subData.planName || subData.plan_name || subData.plan,
      organizationId: subData.organizationId || subData.organization_id,
      status: subData.status,
      startDate: subData.startDate || subData.start_date || subData.created_at,
      endDate: subData.endDate || subData.end_date,
      autoRenew: subData.autoRenew !== undefined ? subData.autoRenew : (subData.auto_renew !== undefined ? subData.auto_renew : false),
      stripeSubscriptionId: subData.stripeSubscriptionId || subData.stripe_subscription_id,
      stripeCustomerId: subData.stripeCustomerId || subData.stripe_customer_id,
      currentPeriodStart: subData.currentPeriodStart || subData.current_period_start,
      currentPeriodEnd: subData.currentPeriodEnd || subData.current_period_end,
      cancelAtPeriodEnd: subData.cancelAtPeriodEnd || subData.cancel_at_period_end,
      addOns: subData.addOns || subData.add_ons,
      // Include fields from v2 API that were being dropped
      limits: subData.limits,
      currentUsage: subData.currentUsage,
      usage: subData.usage,
      assignedTeamCount: subData.assignedTeamCount,
      hasPaymentMethod: subData.hasPaymentMethod,
      referenceName: subData.referenceName || subData.reference_name || subData.name,
      trialEndsAt: subData.trialEndsAt,
      plan: subData.plan
    };
  }

  /**
   * Normalize invoice data from API response
   */
  private normalizeInvoice(invData: any): Invoice {
    return {
      id: invData.id || invData._id,
      number: invData.number || invData.invoice_number,
      status: invData.status,
      amount: invData.amount || invData.total,
      currency: invData.currency || 'USD',
      dueDate: invData.dueDate || invData.due_date ? new Date(invData.dueDate || invData.due_date) : undefined,
      paidAt: invData.paidAt || invData.paid_at ? new Date(invData.paidAt || invData.paid_at) : undefined,
      periodStart: new Date(invData.periodStart || invData.period_start),
      periodEnd: new Date(invData.periodEnd || invData.period_end),
      description: invData.description,
      lines: invData.lines || invData.line_items,
      downloadUrl: invData.downloadUrl || invData.download_url,
      stripeInvoiceId: invData.stripeInvoiceId || invData.stripe_invoice_id
    };
  }
}

// Export singleton instance
export const subscriptionService = new SubscriptionService();